import React from "react";
import PropTypes from "prop-types";

/* ─── Palette aligned with Desktop.jsx + PointSelection ─── */
const P = {
  red: "#DC2626",
  redBg: "#FEF2F2",
  redBorder: "#FECACA",
  text: "#111",
  subtle: "#9CA3AF",
  subtleMid: "#6B7280",
  border: "#F0F0F0",
  borderMid: "#E5E7EB",
  surface: "#fff",
  surfaceAlt: "#FAFAFA",
  chip: "#F7F7F8",
  chipBorder: "#EFEFEF",
  greenBg: "#F0FDF4",
  greenBorder: "#BBF7D0",
  greenText: "#059669",
  greenDark: "#065F46",
  blueBg: "#EFF6FF",
  blueText: "#2563EB",
};

/* ─── Helpers ─── */
const getReadableDate = (dateString) => {
  if (!dateString) return "N/A";
  const [y, m, d] = dateString.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

const money = (n) =>
  (typeof n === "number" ? n : 0).toLocaleString("en-LK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const sortSeats = (seats = []) =>
  [...seats].sort((a, b) => {
    const aNum = Number(String(a).match(/\d+/)?.[0] ?? a);
    const bNum = Number(String(b).match(/\d+/)?.[0] ?? b);

    if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && aNum !== bNum) {
      return aNum - bNum;
    }

    return String(a).localeCompare(String(b), undefined, { numeric: true });
  });

/* ─── Check icon ─── */
const CheckIcon = ({ color = "#fff", size = 9 }) => (
  <svg width={size} height={size} viewBox="0 0 10 10" fill="none">
    <path
      d="M2 5l2.5 2.5L8 3"
      stroke={color}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ─── Section divider ─── */
const Divider = ({ margin = "2px 0" }) => (
  <div style={{ height: 1, background: P.border, margin }} />
);

/* ─── Point card ─── */
const PointCard = ({ label, point, colorScheme = "red" }) => {
  const selected = !!point;
  const isGreen = colorScheme === "green";

  const bg = selected ? (isGreen ? P.greenBg : P.redBg) : P.surfaceAlt;
  const border = selected ? (isGreen ? P.greenBorder : P.redBorder) : P.border;
  const accentColor = isGreen ? P.greenText : P.red;
  const timeChipBg = isGreen ? P.greenBg : P.redBg;
  const timeChipColor = isGreen ? P.greenText : P.red;

  return (
    <div
      style={{
        borderRadius: 12,
        border: `1px solid ${border}`,
        background: bg,
        padding: "9px 11px",
        transition: "border-color 0.15s, background 0.15s",
      }}
    >
      {/* Label row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          marginBottom: 5,
        }}
      >
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: selected ? accentColor : P.border,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "background 0.15s",
          }}
        >
          {selected && <CheckIcon />}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: selected ? accentColor : P.subtle,
          }}
        >
          {label}
        </span>
      </div>

      {/* Content */}
      {selected ? (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: P.text,
                margin: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                letterSpacing: "-0.01em",
                lineHeight: 1.35,
              }}
              title={point.point}
            >
              {point.point}
            </p>
            {point.city && (
              <span
                style={{
                  display: "inline-block",
                  marginTop: 4,
                  fontSize: 10,
                  fontWeight: 500,
                  color: P.subtleMid,
                  background: P.chip,
                  border: `1px solid ${P.chipBorder}`,
                  borderRadius: 5,
                  padding: "1px 7px",
                }}
              >
                {point.city}
              </span>
            )}
          </div>
          {point.time && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: timeChipColor,
                background: timeChipBg,
                borderRadius: 8,
                padding: "3px 9px",
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
                letterSpacing: "-0.01em",
              }}
            >
              {point.time}
            </span>
          )}
        </div>
      ) : (
        <p style={{ fontSize: 12, color: P.subtle, margin: 0 }}>
          Not selected yet
        </p>
      )}
    </div>
  );
};

/* ─── Main ─── */
const BookingSummary = ({
  bus,
  selectedSeats,
  date,
  basePrice,
  convenienceFee,
  totalPrice,
  onProceed,
  boardingPoint,
  droppingPoint,
}) => {
  const hasSelection =
    selectedSeats.length > 0 && !!boardingPoint && !!droppingPoint;

  const busType = bus?.busType || "";
  const isAC = /(^|\s)ac(\s|$)/i.test(busType);
  const sortedSelectedSeats = sortSeats(selectedSeats);

  return (
    <div
      style={{
        background: P.surface,
        borderRadius: 16,
        border: `1px solid ${P.borderMid}`,
        boxShadow: "0 2px 10px rgba(16,24,40,0.045)",
        padding: "18px",
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          marginBottom: 14,
          paddingBottom: 12,
          borderBottom: `1px solid ${P.border}`,
        }}
      >
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: P.subtle,
            margin: "0 0 6px",
          }}
        >
          Booking Summary
        </p>

        {bus?.name && (
          <div>
            <p
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: P.text,
                margin: "0 0 4px",
                letterSpacing: "-0.01em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: 1.35,
              }}
            >
              {bus.name}
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              {bus?.from && bus?.to && (
                <span style={{ fontSize: 11, color: P.subtle }}>
                  {bus.from} → {bus.to}
                </span>
              )}
              {isAC ? (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: P.blueText,
                    background: P.blueBg,
                    borderRadius: 5,
                    padding: "2px 7px",
                  }}
                >
                  AC
                </span>
              ) : busType ? (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: P.subtleMid,
                    background: P.chip,
                    border: `1px solid ${P.chipBorder}`,
                    borderRadius: 5,
                    padding: "2px 7px",
                  }}
                >
                  {busType}
                </span>
              ) : null}

              {/* Journey date chip */}
              {date && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: P.subtleMid,
                    background: "#FFFBEB",
                    border: "1px solid #FDE68A",
                    borderRadius: 5,
                    padding: "2px 7px",
                  }}
                >
                  {getReadableDate(date)}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Body ── */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {/* Boarding + Dropping */}
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          <PointCard
            label="Boarding Point"
            point={boardingPoint}
            colorScheme="green"
          />
          <PointCard
            label="Dropping Point"
            point={droppingPoint}
            colorScheme="red"
          />
        </div>

        <Divider margin="2px 0 0" />

        {/* Selected Seats */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: P.subtle,
              }}
            >
              Selected Seats
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: selectedSeats.length > 0 ? P.red : P.subtle,
                background: selectedSeats.length > 0 ? P.redBg : P.chip,
                border: `1px solid ${
                  selectedSeats.length > 0 ? P.redBorder : P.chipBorder
                }`,
                borderRadius: 20,
                padding: "1px 9px",
                minWidth: 22,
                textAlign: "center",
              }}
            >
              {selectedSeats.length}
            </span>
          </div>

          {selectedSeats.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sortedSelectedSeats.map((seat) => (
                <span
                  key={seat}
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: P.red,
                    background: P.redBg,
                    border: `1px solid ${P.redBorder}`,
                    borderRadius: 7,
                    padding: "3px 10px",
                    letterSpacing: "0.01em",
                    lineHeight: 1.2,
                  }}
                >
                  {seat}
                </span>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: 12, color: P.subtle, margin: 0 }}>
              No seats selected yet
            </p>
          )}
        </div>

        <Divider margin="0" />

        {/* Price breakdown */}
        {selectedSeats.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 7,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 12, color: P.subtleMid }}>
                Subtotal
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: P.text,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                Rs. {money(basePrice)}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 12, color: P.subtleMid }}>
                Convenience Fee
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: P.text,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                Rs. {money(convenienceFee)}
              </span>
            </div>

            <div
              style={{
                height: 1,
                background: P.border,
                margin: "2px 0 0",
              }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 2,
                padding: "10px 12px",
                borderRadius: 12,
                background: "#FFF8F8",
                border: `1px solid ${P.redBorder}`,
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: P.text,
                }}
              >
                Total
              </span>
              <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: P.red,
                  }}
                >
                  Rs.
                </span>
                <span
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: P.red,
                    letterSpacing: "-0.03em",
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1,
                  }}
                >
                  {money(totalPrice)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Footer CTA ── */}
      {onProceed && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: `1px solid ${P.border}`,
          }}
        >
          <button
            onClick={onProceed}
            disabled={!hasSelection}
            style={{
              width: "100%",
              padding: "13px 0",
              borderRadius: 12,
              border: "none",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.01em",
              color: "#fff",
              background: hasSelection ? P.red : "#D1D5DB",
              boxShadow: hasSelection
                ? "0 4px 16px rgba(220,38,38,0.25)"
                : "none",
              cursor: hasSelection ? "pointer" : "not-allowed",
              opacity: hasSelection ? 1 : 0.6,
              transition: "opacity 0.15s, box-shadow 0.15s, transform 0.15s",
            }}
            onMouseEnter={(e) => {
              if (hasSelection) e.currentTarget.style.opacity = "0.88";
            }}
            onMouseLeave={(e) => {
              if (hasSelection) e.currentTarget.style.opacity = "1";
            }}
            onMouseDown={(e) => {
              if (hasSelection) e.currentTarget.style.transform = "scale(0.98)";
            }}
            onMouseUp={(e) => {
              if (hasSelection) e.currentTarget.style.transform = "scale(1)";
            }}
          >
            Proceed to Payment
          </button>

          {!hasSelection && (
            <p
              style={{
                fontSize: 11,
                color: P.subtle,
                textAlign: "center",
                marginTop: 8,
                margin: "8px 0 0",
              }}
            >
              {selectedSeats.length === 0
                ? "Select seats to continue"
                : !boardingPoint
                ? "Choose a boarding point"
                : "Choose a dropping point"}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── PropTypes ─── */
CheckIcon.propTypes = {
  color: PropTypes.string,
  size: PropTypes.number,
};

Divider.propTypes = {
  margin: PropTypes.string,
};

PointCard.propTypes = {
  label: PropTypes.string.isRequired,
  point: PropTypes.object,
  colorScheme: PropTypes.oneOf(["red", "green"]),
};

BookingSummary.propTypes = {
  bus: PropTypes.object.isRequired,
  selectedSeats: PropTypes.arrayOf(PropTypes.string).isRequired,
  date: PropTypes.string.isRequired,
  basePrice: PropTypes.number.isRequired,
  convenienceFee: PropTypes.number.isRequired,
  totalPrice: PropTypes.number.isRequired,
  onProceed: PropTypes.func,
  boardingPoint: PropTypes.object,
  droppingPoint: PropTypes.object,
};

export default BookingSummary;

